import React, { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { ChatBubbleOutline } from '@mui/icons-material';
import { Avatar, Box, Button, Divider, Link, Paper, Stack, Typography } from '@mui/material';

import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { EmployeeUserOption } from '~/hooks/apis/users/useGetEmployeeUsers';

import { formatDateShortWithClock } from '~/services/TimeService';

import { FeedMention } from '~/types/contracting/feed';

import ChatterFieldChanges from './ChatterFieldChanges';
import ChatterMentionInput from './ChatterMentionInput';
import { ChatterDraft, ChatterPost } from './types';
import {
  CHATTER_HASHTAG_CLASS,
  CHATTER_MENTION_CLASS,
  addMention,
  draftLimitMessage,
  getInitials,
  highlightMentionsAndHashtags,
  mentionsInText,
} from './utils';

const RICH_TEXT_SX = {
  typography: 'body2',
  [`& .${CHATTER_MENTION_CLASS}`]: { color: 'primary.main', fontWeight: 500 },
  [`& .${CHATTER_HASHTAG_CLASS}`]: { color: 'info.main' },
};

interface Props {
  post: ChatterPost;
  currentUserName: string;
  isSendingComment: boolean;
  salesforceUrl?: string;
  // Resolves true once the comment is queued, so the input can be cleared.
  onAddComment: (postId: string, draft: ChatterDraft) => Promise<boolean>;
}

export default function ChatterPostCard({
  post,
  currentUserName,
  isSendingComment,
  salesforceUrl,
  onAddComment,
}: Props) {
  const [commentText, setCommentText] = useState('');
  const [mentions, setMentions] = useState<Array<FeedMention>>([]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());
  const commentInputId = `chatter-comment-${post.id}`;
  const isRecordUpdate = post.kind === 'record_update';
  const limitMessage = draftLimitMessage(commentText.trim(), mentionsInText(mentions, commentText));
  const hiddenCommentCount = post.commentCount - post.comments.length;

  const handleSubmitComment = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    const text = commentText.trim();
    if (!text || limitMessage || isSendingComment) {
      return;
    }
    if (await onAddComment(post.id, { text, mentions: mentionsInText(mentions, text), idempotencyKey })) {
      setCommentText('');
      setMentions([]);
      setIdempotencyKey(uuidv4());
    }
  };

  const handleMention = (employee: EmployeeUserOption) => setMentions((prev) => addMention(prev, employee));

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar>{getInitials(post.authorName)}</Avatar>
          <Box>
            <Typography variant="subtitle2">
              {post.authorName}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                {isRecordUpdate ? 'updated this record.' : `to ${post.audience}`}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateShortWithClock(post.createdAt)}
            </Typography>
          </Box>
        </Stack>

        {isRecordUpdate && <ChatterFieldChanges changes={post.changes} moreUpdateCount={post.moreUpdateCount} />}
        {/* A record update has text only when it carries a message, e.g. a case's Change Status. */}
        {(!isRecordUpdate || post.text) && (
          <MarkdownPreview
            content={highlightMentionsAndHashtags(post.text)}
            boxProps={{ sx: { ...RICH_TEXT_SX, mt: 1.5 } }}
          />
        )}

        <Stack direction="row" alignItems="center" spacing={1} mt={1.5}>
          <Button
            size="small"
            startIcon={<ChatBubbleOutline />}
            onClick={() => document.getElementById(commentInputId)?.focus()}
          >
            Comment
          </Button>
          {post.likeCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {post.likeCount} like{post.likeCount === 1 ? '' : 's'}
            </Typography>
          )}
          {post.commentCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {post.commentCount} comment{post.commentCount === 1 ? '' : 's'}
            </Typography>
          )}
        </Stack>
      </Box>
      <Divider />
      <Stack spacing={1.5} p={2} bgcolor="action.hover">
        {hiddenCommentCount > 0 && salesforceUrl && (
          <Link href={salesforceUrl} target="_blank" rel="noopener noreferrer" variant="body2">
            View all {post.commentCount} comments in Salesforce
          </Link>
        )}
        {post.comments.map((comment) => (
          <Stack key={comment.id} direction="row" spacing={1.5}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{getInitials(comment.authorName)}</Avatar>
            <Box>
              <Typography variant="subtitle2">{comment.authorName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateShortWithClock(comment.createdAt)}
              </Typography>
              <MarkdownPreview content={highlightMentionsAndHashtags(comment.text)} boxProps={{ sx: RICH_TEXT_SX }} />
            </Box>
          </Stack>
        ))}
        <form onSubmit={handleSubmitComment}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{getInitials(currentUserName)}</Avatar>
            <ChatterMentionInput
              id={commentInputId}
              placeholder={isSendingComment ? 'Sending…' : 'Write a comment...'}
              value={commentText}
              onChange={setCommentText}
              onMention={handleMention}
            />
          </Stack>
          {limitMessage && (
            <Typography variant="caption" color="error" sx={{ ml: 5.5 }}>
              {limitMessage}
            </Typography>
          )}
        </form>
      </Stack>
    </Paper>
  );
}
